import { EtilizeProductModel } from '@peerless/data-access/etilize-api-client';
import he from 'he';

export interface ProductJsonLdProps {
  product: EtilizeProductModel;
  url: string;
  price?: {
    amount: number;
    currency: string;
  };
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  brand?: string;
}

/**
 * Generates Schema.org Product structured data for SEO.
 * Returns null if no price is provided, as Google requires pricing for Product rich results.
 * @see https://schema.org/Product
 */
export function ProductJsonLd({
  product,
  url,
  price,
  availability = 'InStock',
  brand = 'Peerless-AV',
}: ProductJsonLdProps) {
  // Google requires price for Product rich results
  if (!price) {
    return null;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: he.decode(product.title),
    description: he.decode(product.description.short),
    sku: product.mfgPartNumber,
    mpn: product.mfgPartNumber,
    ...(product.images.length > 0 && {
      image: product.images.map(img => img.src),
    }),
    url,
    brand: {
      '@type': 'Brand',
      name: brand,
    },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: price.currency,
      price: price.amount,
      availability: `https://schema.org/${availability}`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}
